import {
  Drawer,
  Box,
  Stack,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  ListItemBaseProps,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { MenuOpen, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useAppDispatch } from "../../hooks/useDispatch";
import { useAppSelector } from "../../hooks/useSelector";
import { actions } from "../../store/reducers/global";
import { Link as RouterLink } from "react-router-dom";
import Logo from "../../assets/images/dekostel.jpg";

interface ListItemProps extends ListItemBaseProps {
  label: string;
  href: string;
  itemId: string;
}

const ListItem = (props: ListItemProps) => {
  const { label, href, itemId, ...others } = props;
  const { menuOpen } = useAppSelector((state: { global: any }) => state.global);
  const dispatch = useAppDispatch();

  const selected = menuOpen.findIndex((id: any) => id === itemId) > -1;

  const itemHandler = () => {
    dispatch(actions.openMenu(itemId));
    dispatch(actions.closeSidebar());
  };

  // Active menu item on page load
  useEffect(() => {
    const currentPath = document.location.pathname.toString().split("/");

    const foundIndex = currentPath.findIndex((item) => item === itemId);
    if (foundIndex > -1) {
      dispatch(actions.openMenu(itemId));
    }
    // eslint-disable-next-line
  }, [document.location.pathname]);

  return (
    <ListItemButton
      {...others}
      component={RouterLink}
      to={href}
      selected={selected}
      sx={{ mx:2, pl:1, borderRadius: "8px", ...others?.sx }}
      onClick={itemHandler}
    >
      <ListItemText
        primary={
          <Typography
            variant="body2"
            fontWeight={selected ? "700" : "400"}
            color={selected ? "secondary" : "grey.600"}
          >
            {label}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

type ListNestedItemProps = {
  label: string;
  children: any[];
  itemId: string;
};

const ListNestedItem = (props: ListNestedItemProps) => {
  const { label, children, itemId } = props;
  const [open, setOpen] = useState<boolean>(false);

  // Active menu item on page load
  useEffect(() => {
    const currentPath = document.location.pathname.toString().split("/");
    const foundIndex = currentPath.findIndex((item) => item === itemId);
    if (foundIndex > -1) {
      setOpen(true);
    } else {
      setOpen(false);
    }

    // eslint-disable-next-line
  }, [document.location.pathname]);

  return (
    <Box>
      <ListItemButton onClick={() => setOpen((prev) => !prev)} sx={{ mx: 1 }}>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight="400">
              {label}
            </Typography>
          }
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open}>
        <List component="div" disablePadding>
          {children?.map((item: any, i: number) => (
            <Box key={i} sx={{ pl: 2 }}>
              {item.children ? (
                <ListNestedItem
                  label={item.label}
                  children={item.children}
                  itemId={item.slug}
                />
              ) : (
                <ListItem
                  label={item.label}
                  href={item.href}
                  itemId={item.slug}
                  sx={{ pl: 1 }}
                />
              )}
            </Box>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default function DrawerSidebar() {
  const dispatch = useAppDispatch();
  const { sidebar, menu } = useAppSelector((state: any) => state.global);
  const [sideMenu, setSideMenu] = useState<any[]>([]);

  // Function to build nested menus based on 'order'
  const buildNestedData: any = useCallback((menuData: any[], parentOrder: string = "") => {
    return menuData
      .filter(
        (item) =>
          item.is_read &&
          item.order.startsWith(parentOrder) &&
          item.order.length === parentOrder.length + 2
      )
      .map((item) => {
        const children = buildNestedData(menuData, item.order);

        return {
          id: item.order,
          label: item.name,
          href: item.link,
          slug: item.slug,
          permissions: {
            read: item.is_read,
            create: item.is_create,
            update: item.is_update,
            delete: item.is_delete,
          },
          children: children.length > 0 ? children : null,
        };
      });
  }, []);

  useEffect(() => {
    setSideMenu(buildNestedData(menu));
  }, [menu, buildNestedData]);

  // Recursive render function for side menu
  const renderSideMenu = useCallback((item: any, i: number) => {
    return item.children ? (
      <ListNestedItem
        key={i}
        label={item.label}
        children={item.children}
        itemId={item.slug}
      />
    ) : (
      <ListItem
        key={i}
        label={item.label}
        itemId={item.slug}
        href={item.href}
      />
    );
  }, []);

  return (
    <Box>
      <Box
        sx={(theme) => ({
          width: "250px",
          flexShrink: 0,
          position: "fixed",
          left: 0,
          bottom: 0,
          top: "67px",
          height: "calc(100vh - 70px)",
          overflowY: "auto",
          [theme.breakpoints.down("md")]: {
            display: "none",
          },
        })}
      >
        <Box
          sx={{
            backgroundColor: "#c9ffe5",
            borderRightWidth: 1,
            borderRightColor: "#d3c9e0",
            borderRightStyle: "solid",
            pt: 2,
            height: "100%",
          }}
          role="presentation"
        >
          <List>{sideMenu.map(renderSideMenu)}</List>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={sidebar}
        onClose={() => dispatch(actions.closeSidebar())}
      >
        <Box
          sx={{
            width: 250,
            backgroundColor: "#c9ffe5",
            borderRightColor: "#d3c9e0",
            borderRightStyle: "solid",
          }}
          role="presentation"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1 }}
          >
            <RouterLink to="/app">
              <img src={Logo} style={{ height: 24 }} alt="Logo" />
            </RouterLink>

            <IconButton
              size="small"
              color="primary"
              onClick={() => dispatch(actions.closeSidebar())}
            >
              <MenuOpen />
            </IconButton>
          </Stack>

          <List>{sideMenu.map(renderSideMenu)}</List>
        </Box>
      </Drawer>
    </Box>
  );
}
